/**
 * Portfolio Management Service
 *
 * Allows users to create and manage portfolios of BMUs (plants)
 * for combined performance tracking and P&L analysis.
 */

const PORTFOLIO_STORAGE_KEY = 'energycosmos_portfolios';

// Estimated marginal costs by fuel type (£/MWh) - used for P&L estimation
// These are rough estimates and should be configurable in a production system
const FUEL_MARGINAL_COSTS = {
  CCGT: 45,      // Combined Cycle Gas Turbine - gas + carbon
  OCGT: 65,      // Open Cycle Gas Turbine - less efficient
  COAL: 55,      // Coal + carbon (legacy)
  NUCLEAR: 10,   // Low marginal cost
  WIND: 5,       // Near-zero marginal (O&M)
  SOLAR: 3,      // Near-zero marginal
  HYDRO: 8,      // Low marginal
  BIOMASS: 35,   // Biomass fuel costs
  PS: 25,        // Pumped storage (efficiency losses)
  NPSHYD: 8,     // Non-pumped storage hydro
  BATTERY: 15,   // Battery degradation costs
  OTHER: 40,     // Default
};
const ACTIVE_PORTFOLIO_KEY = 'energycosmos_active_portfolio';
const FIRST_RUN_KEY = 'energycosmos_first_run_completed';

// Sample BMUs for demo portfolio - representative mix of UK generators
const SAMPLE_BMUS = [
  { bmuId: 'T_DAMC-1', name: 'Damhead Creek', fuelType: 'CCGT', capacity: 805 },
  { bmuId: 'T_COTPS-1', name: 'Cottam', fuelType: 'CCGT', capacity: 400 },
  { bmuId: 'T_DIDCB6', name: 'Didcot B', fuelType: 'CCGT', capacity: 680 },
  { bmuId: 'T_PEMB-1', name: 'Pembroke', fuelType: 'CCGT', capacity: 2200 },
  { bmuId: 'T_RATSGT-1', name: 'Ratcliffe', fuelType: 'COAL', capacity: 500 },
  { bmuId: 'E_MARK-1', name: 'Markham Grange', fuelType: 'WIND', capacity: 68 },
  { bmuId: 'T_DRAXX-1', name: 'Drax', fuelType: 'BIOMASS', capacity: 645 },
  { bmuId: 'T_SIZB-1', name: 'Sizewell B', fuelType: 'NUCLEAR', capacity: 1198 },
  { bmuId: 'T_FFES-1', name: 'Ffestiniog', fuelType: 'PS', capacity: 360 },
  { bmuId: 'E_GWYNT-1', name: 'Gwynt y Mor', fuelType: 'WIND', capacity: 576 },
];

/**
 * Check if this is first run (no portfolios exist)
 */
export const isFirstRun = () => {
  try {
    const completed = localStorage.getItem(FIRST_RUN_KEY);
    if (completed === 'true') return false;
    const portfolios = getPortfolios();
    return portfolios.length === 0;
  } catch (e) {
    return true;
  }
};

/**
 * Mark first run as completed
 */
export const markFirstRunComplete = () => {
  try {
    localStorage.setItem(FIRST_RUN_KEY, 'true');
  } catch (e) {
    console.warn('Failed to mark first run complete:', e);
  }
};

/**
 * Create a sample portfolio with representative BMUs
 */
export const createSamplePortfolio = () => {
  try {
    const samplePortfolio = createPortfolio('My Portfolio', SAMPLE_BMUS);
    setActivePortfolio(samplePortfolio.id);
    markFirstRunComplete();
    return samplePortfolio;
  } catch (e) {
    console.error('Failed to create sample portfolio:', e);
    return null;
  }
};

/**
 * Get sample BMUs for selection
 */
export const getSampleBMUs = () => SAMPLE_BMUS;

/**
 * Get all saved portfolios
 */
export const getPortfolios = () => {
  try {
    const stored = localStorage.getItem(PORTFOLIO_STORAGE_KEY);
    return stored ? JSON.parse(stored) : [];
  } catch (e) {
    console.warn('Failed to load portfolios:', e);
    return [];
  }
};

/**
 * Save portfolios to localStorage
 */
const savePortfolios = (portfolios) => {
  try {
    localStorage.setItem(PORTFOLIO_STORAGE_KEY, JSON.stringify(portfolios));
    return true;
  } catch (e) {
    console.warn('Failed to save portfolios:', e);
    return false;
  }
};

/**
 * Create a new portfolio
 * @param {string} name - Portfolio name
 * @param {Array} plants - Array of plant objects { bmuId, name, fuelType, capacity }
 */
export const createPortfolio = (name, plants = []) => {
  const portfolios = getPortfolios();

  // Check for duplicate name
  if (portfolios.some(p => p.name.toLowerCase() === name.toLowerCase())) {
    throw new Error(`Portfolio "${name}" already exists`);
  }

  const newPortfolio = {
    id: `portfolio_${Date.now()}`,
    name,
    plants: plants.map(p => ({
      bmuId: p.bmuId,
      name: p.name,
      fuelType: p.fuelType,
      capacity: p.capacity,
    })),
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
  };

  portfolios.push(newPortfolio);
  savePortfolios(portfolios);

  return newPortfolio;
};

/**
 * Update an existing portfolio
 */
export const updatePortfolio = (portfolioId, updates) => {
  const portfolios = getPortfolios();
  const index = portfolios.findIndex(p => p.id === portfolioId);

  if (index === -1) {
    throw new Error('Portfolio not found');
  }

  // Check for duplicate name if name is being changed
  if (updates.name && updates.name !== portfolios[index].name) {
    if (portfolios.some(p => p.id !== portfolioId && p.name.toLowerCase() === updates.name.toLowerCase())) {
      throw new Error(`Portfolio "${updates.name}" already exists`);
    }
  }

  portfolios[index] = {
    ...portfolios[index],
    ...updates,
    updatedAt: new Date().toISOString(),
  };

  savePortfolios(portfolios);
  return portfolios[index];
};

/**
 * Delete a portfolio
 */
export const deletePortfolio = (portfolioId) => {
  const portfolios = getPortfolios();
  const filtered = portfolios.filter(p => p.id !== portfolioId);

  if (filtered.length === portfolios.length) {
    throw new Error('Portfolio not found');
  }

  savePortfolios(filtered);

  // Clear active portfolio if it was deleted
  if (getActivePortfolioId() === portfolioId) {
    clearActivePortfolio();
  }

  return true;
};

/**
 * Add a plant to a portfolio
 */
export const addPlantToPortfolio = (portfolioId, plant) => {
  const portfolios = getPortfolios();
  const portfolio = portfolios.find(p => p.id === portfolioId);

  if (!portfolio) {
    throw new Error('Portfolio not found');
  }

  // Check if plant already exists
  if (portfolio.plants.some(p => p.bmuId === plant.bmuId)) {
    throw new Error(`${plant.name} is already in this portfolio`);
  }

  portfolio.plants.push({
    bmuId: plant.bmuId,
    name: plant.name,
    fuelType: plant.fuelType,
    capacity: plant.capacity,
  });

  portfolio.updatedAt = new Date().toISOString();
  savePortfolios(portfolios);

  return portfolio;
};

/**
 * Remove a plant from a portfolio
 */
export const removePlantFromPortfolio = (portfolioId, bmuId) => {
  const portfolios = getPortfolios();
  const portfolio = portfolios.find(p => p.id === portfolioId);

  if (!portfolio) {
    throw new Error('Portfolio not found');
  }

  portfolio.plants = portfolio.plants.filter(p => p.bmuId !== bmuId);
  portfolio.updatedAt = new Date().toISOString();
  savePortfolios(portfolios);

  return portfolio;
};

/**
 * Get the active portfolio ID
 */
export const getActivePortfolioId = () => {
  try {
    return localStorage.getItem(ACTIVE_PORTFOLIO_KEY);
  } catch (e) {
    return null;
  }
};

/**
 * Set the active portfolio
 */
export const setActivePortfolio = (portfolioId) => {
  try {
    localStorage.setItem(ACTIVE_PORTFOLIO_KEY, portfolioId);
    return true;
  } catch (e) {
    console.warn('Failed to set active portfolio:', e);
    return false;
  }
};

/**
 * Clear the active portfolio
 */
export const clearActivePortfolio = () => {
  try {
    localStorage.removeItem(ACTIVE_PORTFOLIO_KEY);
    return true;
  } catch (e) {
    return false;
  }
};

/**
 * Get the active portfolio with full details
 */
export const getActivePortfolio = () => {
  const activeId = getActivePortfolioId();
  if (!activeId) return null;

  const portfolios = getPortfolios();
  return portfolios.find(p => p.id === activeId) || null;
};

/**
 * Get marginal cost for a fuel type
 */
const getMarginalCost = (fuelType) => {
  if (!fuelType) return FUEL_MARGINAL_COSTS.OTHER;
  const upperFuel = fuelType.toUpperCase();
  return FUEL_MARGINAL_COSTS[upperFuel] || FUEL_MARGINAL_COSTS.OTHER;
};

/**
 * Calculate portfolio performance from BOA data
 * @param {Object} portfolio - Portfolio object
 * @param {Array} boaData - BOA data for the day
 * @param {Array} systemPrices - System prices for the day
 */
export const calculatePortfolioPerformance = (portfolio, boaData, systemPrices) => {
  if (!portfolio || !portfolio.plants || portfolio.plants.length === 0) {
    return null;
  }

  const plantBmuIds = new Set(portfolio.plants.map(p => p.bmuId));
  const plantFuelTypes = {};
  portfolio.plants.forEach(p => {
    plantFuelTypes[p.bmuId] = p.fuelType;
  });

  // Build price lookup (both sell and buy prices for imbalance calculation)
  const pricesBySP = {};
  const sellPricesBySP = {};
  const buyPricesBySP = {};
  systemPrices.forEach(p => {
    const sp = p.settlementPeriod;
    if (sp) {
      pricesBySP[sp] = p.systemSellPrice || p.systemBuyPrice || p.imbalancePriceAmountGBP || 0;
      sellPricesBySP[sp] = p.systemSellPrice || 0;
      buyPricesBySP[sp] = p.systemBuyPrice || 0;
    }
  });

  // Filter BOAs for portfolio plants
  const portfolioBoas = boaData.filter(b => {
    const bmuId = b.bmUnit || b.nationalGridBmUnit || '';
    return plantBmuIds.has(bmuId);
  });

  // Calculate metrics per plant
  const plantMetrics = {};
  portfolio.plants.forEach(plant => {
    plantMetrics[plant.bmuId] = {
      bmuId: plant.bmuId,
      name: plant.name,
      fuelType: plant.fuelType,
      capacity: plant.capacity || 0,
      boaCount: 0,
      offerVolume: 0,
      bidVolume: 0,
      totalVolume: 0,
      estimatedRevenue: 0,
      estimatedCost: 0,
      grossMargin: 0,
      imbalanceCost: 0,
      netPnL: 0,
      avgDispatchPrice: 0,
      priceSum: 0,
      priceCount: 0,
      marginalCost: getMarginalCost(plant.fuelType),
    };
  });

  // Process BOAs
  portfolioBoas.forEach(boa => {
    const bmuId = boa.bmUnit || boa.nationalGridBmUnit;
    if (!plantMetrics[bmuId]) return;

    const pm = plantMetrics[bmuId];
    const levelFrom = boa.levelFrom ?? 0;
    const levelTo = boa.levelTo ?? 0;
    const delta = levelTo - levelFrom;
    const volume = Math.abs(delta);
    const isOffer = delta > 0; // Offer = increase generation

    pm.boaCount++;
    pm.totalVolume += volume;

    if (isOffer) {
      pm.offerVolume += volume;
    } else {
      pm.bidVolume += volume;
    }

    // Get price at dispatch
    const sp = boa.settlementPeriod || boa.settlementPeriodFrom || 1;
    const priceAtDispatch = pricesBySP[sp] || pricesBySP[String(sp)] || 0;
    const sellPrice = sellPricesBySP[sp] || sellPricesBySP[String(sp)] || 0;
    const buyPrice = buyPricesBySP[sp] || buyPricesBySP[String(sp)] || 0;

    if (priceAtDispatch > 0) {
      pm.priceSum += priceAtDispatch;
      pm.priceCount++;

      // Revenue calculation
      // For offers (generation increase): revenue = volume × price
      // For bids (generation decrease): revenue = volume × price (payment received for reducing)
      pm.estimatedRevenue += volume * priceAtDispatch;

      // Cost calculation
      // For offers: cost = volume × marginal fuel cost (we're generating more)
      // For bids: cost = 0 (we're reducing, saving fuel)
      if (isOffer) {
        pm.estimatedCost += volume * pm.marginalCost;
      }

      // Imbalance cost estimation
      // This represents potential exposure if plant doesn't deliver as instructed
      // Simplified: assume 5% potential deviation at spread between buy/sell prices
      const spread = Math.abs(buyPrice - sellPrice);
      pm.imbalanceCost += volume * 0.05 * spread;
    }
  });

  // Calculate aggregates and P&L per plant
  let totalBoaCount = 0;
  let totalOfferVolume = 0;
  let totalBidVolume = 0;
  let totalVolume = 0;
  let totalRevenue = 0;
  let totalCost = 0;
  let totalImbalanceCost = 0;
  let activePlants = 0;

  Object.values(plantMetrics).forEach(pm => {
    // Calculate averages
    if (pm.priceCount > 0) {
      pm.avgDispatchPrice = pm.priceSum / pm.priceCount;
    }

    // Calculate P&L metrics
    pm.grossMargin = pm.estimatedRevenue - pm.estimatedCost;
    pm.netPnL = pm.grossMargin - pm.imbalanceCost;
    pm.marginPercent = pm.estimatedRevenue > 0
      ? (pm.grossMargin / pm.estimatedRevenue) * 100
      : 0;

    // Aggregate totals
    totalBoaCount += pm.boaCount;
    totalOfferVolume += pm.offerVolume;
    totalBidVolume += pm.bidVolume;
    totalVolume += pm.totalVolume;
    totalRevenue += pm.estimatedRevenue;
    totalCost += pm.estimatedCost;
    totalImbalanceCost += pm.imbalanceCost;

    if (pm.boaCount > 0) {
      activePlants++;
    }
  });

  const avgPrice = systemPrices.length > 0
    ? systemPrices.reduce((sum, p) => sum + (p.systemSellPrice || p.systemBuyPrice || 0), 0) / systemPrices.length
    : 0;

  // Portfolio-level P&L
  const totalGrossMargin = totalRevenue - totalCost;
  const totalNetPnL = totalGrossMargin - totalImbalanceCost;
  const marginPercent = totalRevenue > 0 ? (totalGrossMargin / totalRevenue) * 100 : 0;

  return {
    portfolioId: portfolio.id,
    portfolioName: portfolio.name,
    plantCount: portfolio.plants.length,
    activePlants,
    totalCapacity: portfolio.plants.reduce((sum, p) => sum + (p.capacity || 0), 0),
    boaCount: totalBoaCount,
    offerVolume: totalOfferVolume,
    bidVolume: totalBidVolume,
    totalVolume,
    // Revenue & P&L metrics
    estimatedRevenue: totalRevenue,
    estimatedCost: totalCost,
    grossMargin: totalGrossMargin,
    imbalanceCost: totalImbalanceCost,
    netPnL: totalNetPnL,
    marginPercent,
    // Price metrics
    avgDispatchPrice: totalBoaCount > 0 ? totalRevenue / totalVolume : 0,
    marketAvgPrice: avgPrice,
    // Per-plant breakdown
    plantMetrics: Object.values(plantMetrics),
  };
};

const portfolioService = {
  getPortfolios,
  createPortfolio,
  updatePortfolio,
  deletePortfolio,
  addPlantToPortfolio,
  removePlantFromPortfolio,
  getActivePortfolioId,
  setActivePortfolio,
  clearActivePortfolio,
  getActivePortfolio,
  calculatePortfolioPerformance,
  isFirstRun,
  markFirstRunComplete,
  createSamplePortfolio,
  getSampleBMUs,
};

export default portfolioService;
